import { PartyBuilder } from '@/components/events/PartyBuilder';
import { Badge } from '@/components/shared/Badge';
import { Button } from '@/components/shared/Button';
import { Panel } from '@/components/shared/Panel';
import { moveSlotWithinRoleGroup, normalizePartySlots } from '@/features/events/builder';
import { formatDateTime } from '@/utils/date';
import type { EventBuilderPartyDraft, TemplateBuilderDraft, WeaponCatalogItem } from '@/types/domain';

interface TemplateBuilderFormProps {
  mode: 'create' | 'edit';
  draft: TemplateBuilderDraft;
  weapons: WeaponCatalogItem[];
  isSavingTemplate: boolean;
  isAutosaving: boolean;
  isDeleting: boolean;
  isTogglingArchive: boolean;
  hasSavedTemplate: boolean;
  hasUnsavedChanges: boolean;
  autosaveStatus: 'idle' | 'saving' | 'saved' | 'restored' | 'error';
  draftRestoredFrom: 'cloud' | 'local' | null;
  lastAutosavedAt?: string;
  lastTemplateSavedAt?: string;
  autosaveError?: string | null;
  onDraftChange: (draft: TemplateBuilderDraft) => void;
  onSave: () => Promise<void>;
  onSaveAsNew: () => Promise<void>;
  onStartNew: () => void;
  onDuplicate: () => void;
  onToggleArchive: () => Promise<void>;
  onDelete?: () => void;
}

function updatePartyDraft(
  draft: TemplateBuilderDraft,
  partyNumber: number,
  updater: (party: EventBuilderPartyDraft) => EventBuilderPartyDraft,
): TemplateBuilderDraft {
  return {
    ...draft,
    parties: draft.parties.map((party) => (party.partyNumber === partyNumber ? updater(party) : party)),
  };
}

function addSlotToTemplateDraft(draft: TemplateBuilderDraft, partyNumber: number): TemplateBuilderDraft {
  return {
    ...draft,
    parties: draft.parties.map((party) =>
      party.partyNumber === partyNumber
        ? {
            ...party,
            slots: normalizePartySlots([
              ...party.slots,
              {
                clientId: typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
                partyNumber,
                slotOrder: party.slots.length + 1,
                note: '',
                roleCategory: 'DPS',
                weaponName: '',
                imagePath: '',
                capacity: 1,
                waitlistEnabled: true,
                isActive: true,
              },
            ]),
          }
        : party,
    ),
  };
}

function renderAutosaveBadge(status: TemplateBuilderFormProps['autosaveStatus']): JSX.Element {
  switch (status) {
    case 'saving':
      return <Badge tone="warning">Saving Draft...</Badge>;
    case 'saved':
      return <Badge tone="success">Draft Saved</Badge>;
    case 'restored':
      return <Badge tone="info">Draft Restored</Badge>;
    case 'error':
      return <Badge tone="danger">Draft Save Failed</Badge>;
    default:
      return <Badge tone="default">Draft Ready</Badge>;
  }
}

export function TemplateBuilderForm({
  mode,
  draft,
  weapons,
  isSavingTemplate,
  isAutosaving,
  isDeleting,
  isTogglingArchive,
  hasSavedTemplate,
  hasUnsavedChanges,
  autosaveStatus,
  draftRestoredFrom,
  lastAutosavedAt,
  lastTemplateSavedAt,
  autosaveError,
  onDraftChange,
  onSave,
  onSaveAsNew,
  onStartNew,
  onDuplicate,
  onToggleArchive,
  onDelete,
}: TemplateBuilderFormProps): JSX.Element {
  const isDraftBusy = isAutosaving || isSavingTemplate;
  const lifecycleDescription = hasSavedTemplate
    ? 'The working draft is separate from the saved template. Update the existing template only when you want to publish the current composition.'
    : 'This working draft is cloud-saved separately from the reusable template record. Publish it when you are ready.';

  return (
    <div className="stack">
      <Panel
        title={mode === 'edit' ? 'Template Session' : 'New Template Session'}
        description={lifecycleDescription}
        action={
          <div className="panel-actions">
            <Button variant="secondary" onClick={onStartNew} disabled={isSavingTemplate || isAutosaving || isDeleting}>
              New Template
            </Button>
            <Button variant="secondary" onClick={onDuplicate} disabled={isDraftBusy || isDeleting}>
              Duplicate Template
            </Button>
            <Button variant="secondary" onClick={() => void onSaveAsNew()} disabled={isDraftBusy || isDeleting}>
              Save As New
            </Button>
            <Button variant="secondary" onClick={() => void onToggleArchive()} disabled={isDraftBusy || isDeleting || isTogglingArchive}>
              {isTogglingArchive ? 'Updating...' : draft.isActive ? 'Archive Template' : 'Reactivate Template'}
            </Button>
            {onDelete ? (
              <Button variant="danger" onClick={onDelete} disabled={isDeleting || isDraftBusy}>
                {isDeleting ? 'Deleting...' : 'Delete Template'}
              </Button>
            ) : null}
            <Button onClick={() => void onSave()} disabled={isDraftBusy || isDeleting}>
              {isSavingTemplate ? 'Saving...' : hasSavedTemplate ? 'Update Existing Template' : 'Save Template'}
            </Button>
          </div>
        }
      >
        <div className="template-session__badges">
          {renderAutosaveBadge(autosaveStatus)}
          <Badge tone={hasSavedTemplate ? (hasUnsavedChanges ? 'warning' : 'success') : 'info'}>
            {hasSavedTemplate ? (hasUnsavedChanges ? 'Unsaved Changes' : 'Synced With Saved Template') : 'Draft Only'}
          </Badge>
          <Badge tone={draft.isActive ? 'success' : 'default'}>{draft.isActive ? 'Active Template' : 'Archived Template'}</Badge>
          {draftRestoredFrom ? <Badge tone="info">Restored From {draftRestoredFrom === 'cloud' ? 'Cloud Draft' : 'Local Backup'}</Badge> : null}
        </div>

        <div className="template-session__meta">
          <div className="template-session__item">
            <span className="profile-summary__label">Working Draft</span>
            <strong>
              {autosaveStatus === 'saving'
                ? 'Saving latest changes...'
                : autosaveStatus === 'error'
                  ? 'Latest draft save failed'
                  : autosaveStatus === 'restored'
                    ? 'Recovered into the editor'
                    : 'Cloud draft is available'}
            </strong>
            <span className="profile-summary__hint">
              {lastAutosavedAt ? `Last draft save ${formatDateTime(lastAutosavedAt)}` : 'Draft has not been autosaved yet.'}
            </span>
          </div>

          <div className="template-session__item">
            <span className="profile-summary__label">Saved Template</span>
            <strong>{hasSavedTemplate ? 'Reusable template record exists' : 'Not published yet'}</strong>
            <span className="profile-summary__hint">
              {lastTemplateSavedAt ? `Last saved ${formatDateTime(lastTemplateSavedAt)}` : 'Save the working draft to create a reusable template.'}
            </span>
          </div>
        </div>

        {autosaveError ? <p className="form-footnote form-footnote--error">{autosaveError}</p> : null}
      </Panel>

      <Panel
        title={draft.id ? 'Edit Mass Template' : 'Create Mass Template'}
        description="Templates define a reusable four-party composition that staff can load straight into new mass creation."
      >
        <div className="form-grid form-grid--two">
          <label className="field">
            <span className="field__label">Template Name</span>
            <input
              className="field__input"
              value={draft.name}
              onChange={(event) => onDraftChange({ ...draft, name: event.target.value })}
              placeholder="20v20 Small Scale Core"
            />
          </label>

          <div className="field field--checkbox">
            <span className="field__label">Template State</span>
            <label className="toggle">
              <input
                type="checkbox"
                checked={draft.isActive}
                onChange={(event) => onDraftChange({ ...draft, isActive: event.target.checked })}
              />
              <span>{draft.isActive ? 'Active' : 'Archived'}</span>
            </label>
          </div>
        </div>

        <label className="field">
          <span className="field__label">Description</span>
          <textarea
            className="field__input field__input--textarea"
            value={draft.description}
            onChange={(event) => onDraftChange({ ...draft, description: event.target.value })}
            placeholder="What this comp is for, when to use it, and any internal notes for officers."
          />
        </label>
      </Panel>

      {draft.parties.map((party) => (
        <PartyBuilder
          key={party.partyNumber}
          party={party}
          weapons={weapons}
          joinedCountBySlotId={{}}
          onLabelChange={(label) => onDraftChange(updatePartyDraft(draft, party.partyNumber, (current) => ({ ...current, label })))}
          onAddSlot={() => onDraftChange(addSlotToTemplateDraft(draft, party.partyNumber))}
          onRemoveSlot={(clientId) =>
            onDraftChange(
              updatePartyDraft(draft, party.partyNumber, (current) => ({
                ...current,
                slots: normalizePartySlots(current.slots.filter((slot) => slot.clientId !== clientId)),
              })),
            )
          }
          onMoveSlot={(clientId, direction) =>
            onDraftChange(
              updatePartyDraft(draft, party.partyNumber, (current) => ({
                ...current,
                slots: moveSlotWithinRoleGroup(current.slots, clientId, direction),
              })),
            )
          }
          onChangeSlot={(clientId, patch) =>
            onDraftChange(
              updatePartyDraft(draft, party.partyNumber, (current) => ({
                ...current,
                slots: normalizePartySlots(current.slots.map((slot) => (slot.clientId === clientId ? { ...slot, ...patch } : slot))),
              })),
            )
          }
        />
      ))}
    </div>
  );
}
